import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from './ui/button'
import { Logo } from './ui/Logo'
import { Input } from './ui/input'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import {
  Plus, Send, Paperclip, Globe, LogOut, Database, Sparkles,
  MessageSquare, Bot, FileText, HelpCircle, Building2, Zap, Crown, Home, Receipt, Shield,
  PanelLeftClose, PanelLeftOpen, Wifi, BookOpen,
} from 'lucide-react'
import { useAuth } from '../hooks/use-auth'
import { useAvatarSrc } from '../hooks/use-avatar-src'
import { useI18n } from '../i18n'
import { get, post } from '../helper/network'
import API from '../helper/api'
import { sendSSE } from './sseApi'

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
}

interface Conversation {
  id: string
  titleName: string
  createTime: string
}

export default function EducationAI() {
  const { user, isLoggedIn, isAdmin, logout } = useAuth()
  const avatarSrc = useAvatarSrc(user?.avatarUrl)
  const { lang, toggleLang, t } = useI18n()
  const navigate = useNavigate()

  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingText, setStreamingText] = useState('')
  const [quota, setQuota] = useState<number | null>(null)
  const [branding, setBranding] = useState<any>(null)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true')
  const [navCollapsed, setNavCollapsed] = useState(false)
  const [deepThink, setDeepThink] = useState(false)
  const [useKnowledge, setUseKnowledge] = useState(true)
  const [webSearch, setWebSearch] = useState(false)
  const [showKbConfirm, setShowKbConfirm] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const appName = branding?.welcomeText || user?.appName || user?.enterpriseName || 'AI管家'
  const appId = user?.appId || 'default'
  const themePrimary = branding?.themePrimaryColor || '#f97316'

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev
      localStorage.setItem('sidebarCollapsed', String(next))
      return next
    })
  }

  // 加载对话历史
  const loadConversations = useCallback(async () => {
    try {
      const username = user?.username
      if (!username) return
      const res: any = await get(API.GET_HISTORY_TITLE_LIST, { createUser: username })
      if (res && res.code === 200 && res.data) {
        setConversations(res.data)
      }
    } catch {}
  }, [user?.username])

  // 加载企业品牌
  const loadBranding = useCallback(async () => {
    try {
      const res: any = await get(API.GET_ENTERPRISE_BRANDING, {})
      if (res && res.code === 200 && res.data) {
        setBranding(res.data)
      }
    } catch {}
  }, [])

  // 加载 Token 余额
  const loadQuota = useCallback(async () => {
    try {
      const res: any = await get(API.GET_QUOTA)
      if (res && res.code === 200) {
        setQuota(res.data)
      }
    } catch {}
  }, [])

  useEffect(() => {
    if (isLoggedIn) {
      loadConversations()
      loadQuota()
      loadBranding()
    }
  }, [isLoggedIn, loadConversations, loadQuota, loadBranding])

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingText])

  // 新建对话
  const handleNewChat = async () => {
    try {
      const username = user?.username || ''
      const res: any = await post(API.POST_HISTORY_TITLE, {
        createUser: username,
        titleName: t('新建对话', 'New Chat'),
      })
      if (res && res.code === 200 && res.data) {
        const newConv: Conversation = {
          id: res.data,
          titleName: t('新建对话', 'New Chat'),
          createTime: new Date().toISOString(),
        }
        setConversations((prev) => [newConv, ...prev])
        setActiveId(newConv.id)
        setMessages([])
        setStreamingText('')
      }
    } catch {}
  }

  // 发送消息
  const handleSend = async (customInput?: string) => {
    const text = customInput || input.trim()
    if (!text || isStreaming) return

    let currentTitleId = activeId

    // 如果没有当前对话，先新建
    if (!currentTitleId) {
      try {
        const username = user?.username || ''
        const res: any = await post(API.POST_HISTORY_TITLE, {
          createUser: username,
          titleName: text.slice(0, 30),
        })
        if (res && res.code === 200 && res.data) {
          const newConv: Conversation = {
            id: res.data,
            titleName: text.slice(0, 30),
            createTime: new Date().toISOString(),
          }
          setConversations((prev) => [newConv, ...prev])
          currentTitleId = res.data
          setActiveId(res.data)
        }
      } catch {
        return
      }
    }

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: text,
    }
    setMessages((prev) => [...prev, userMsg])
    setInput('')
    setIsStreaming(true)
    setStreamingText('')

    const controller = sendSSE(
      {
        appId,
        titleId: currentTitleId,
        content: text,
        deepThink,
        useKnowledge,
        webSearch,
      },
      API.POST_CHAT_DEEPSEEK,
      (chunk: string) => {
        setStreamingText((prev) => prev + chunk)
      },
      () => {
        setStreamingText((prev) => {
          const finalText = prev
          setMessages((prevMsgs) => [
            ...prevMsgs,
            { id: `a-${Date.now()}`, role: 'assistant', content: finalText },
          ])
          return ''
        })
        setIsStreaming(false)
        loadQuota()
      },
      (err: Error) => {
        console.error('SSE error:', err)
        setStreamingText((prev) => {
          if (prev) {
            setMessages((prevMsgs) => [
              ...prevMsgs,
              { id: `a-${Date.now()}`, role: 'assistant', content: prev },
            ])
          }
          return ''
        })
        setIsStreaming(false)
      }
    )
    abortRef.current = controller
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  // 空状态快捷提问
  const quickQuestions = [
    { icon: FileText, label: t('查看公司差旅报销标准', 'Check travel reimbursement policy'), color: 'bg-blue-50 text-blue-600' },
    { icon: HelpCircle, label: t('新员工入职流程是什么', 'What is the onboarding process'), color: 'bg-emerald-50 text-emerald-600' },
    { icon: Bot, label: t('帮我梳理巡检不合格项', 'Help sort out inspection issues'), color: 'bg-orange-50 text-orange-600' },
    { icon: Database, label: t('食品安全规程有哪些', 'What are the food safety procedures'), color: 'bg-violet-50 text-violet-600' },
  ]

  return (
    <div className="h-screen flex flex-col bg-[#fafafa]">
      {/* 顶栏 */}
      <header className="h-14 bg-white/70 backdrop-blur-2xl border-b border-black/[0.04] flex items-center px-4 shrink-0 z-30">
        <Link to="/" className="flex items-center gap-2 text-lg font-semibold mr-4 shrink-0">
          <Logo size={24} />
          <span className="text-[#1a1a1a]">PrimeBook</span>
          <span className="text-orange-500">AI</span>
        </Link>

        <div className="ml-auto flex items-center gap-2">
          {quota !== null && (
            <Link to="/pricing" className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] text-orange-500 hover:bg-orange-50/60 transition-all border border-orange-100/60 hidden sm:inline-flex">
              <Zap className="h-3 w-3" />
              {quota.toLocaleString()}
              <Crown className="h-3 w-3" />
            </Link>
          )}

          <button
            onClick={toggleLang}
            className="flex items-center gap-1 px-2 py-1.5 text-[12px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all"
          >
            <Globe className="h-3.5 w-3.5" />
            {lang === 'zh' ? 'EN' : '中'}
          </button>

          {isLoggedIn && user && (
            <div className="flex items-center gap-2">
              <Link to="/profile" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                <Avatar className="h-7 w-7">
                  {avatarSrc && <AvatarImage src={avatarSrc} alt={user.nickName} />}
                  <AvatarFallback className="bg-orange-500 text-white text-[11px]">
                    {user.nickName?.[0] || user.username?.[0] || 'U'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-[13px] text-gray-500 hidden lg:inline">{user.nickName}</span>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-gray-400 hover:text-[#1a1a1a] text-[13px] h-8 px-2 hidden sm:inline-flex"
              >
                <LogOut className="h-3.5 w-3.5 mr-1" />
                {t('退出', 'Logout')}
              </Button>
            </div>
          )}
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* 左侧栏 */}
        <aside className={`hidden md:flex ${sidebarCollapsed ? 'w-16' : 'w-72'} flex-col border-r border-black/[0.04] bg-white/50 transition-all duration-300 relative`}>
          {/* 右侧边框上的折叠把手 */}
          <button onClick={toggleSidebar}
            title={sidebarCollapsed ? t('展开侧边栏', 'Expand') : t('收起侧边栏', 'Collapse')}
            className="group absolute top-1/2 -translate-y-1/2 z-20 -right-3.5
              flex items-center justify-center
              h-12 w-7 rounded-full
              bg-white border border-black/[0.06]
              shadow-[0_0_0_1px_rgba(0,0,0,0.02),0_1px_4px_rgba(0,0,0,0.06)]
              hover:h-14 hover:border-orange-200/80 hover:shadow-[0_0_12px_rgba(249,115,22,0.15)]
              active:scale-95
              transition-all duration-200">
            <svg className={`h-4 w-4 text-gray-300 group-hover:text-orange-400 transition-all duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 4l-4 4 4 4" />
            </svg>
          </button>

          {/* 企业信息卡 + 管理后台入口 */}
          {(user?.enterpriseName || branding) && (
            <div className={`${sidebarCollapsed ? 'p-2' : 'p-3'}`}>
              <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} p-2.5 rounded-xl bg-gradient-to-r from-orange-50/80 to-amber-50/50`}>
                <Link to="/enterprise" className="shrink-0">
                  <div className="w-9 h-9 rounded-xl bg-orange-500/10 flex items-center justify-center overflow-hidden hover:bg-orange-500/20 transition-colors">
                    {branding?.logoUrl ? (
                      <img src={branding.logoUrl} alt="" className="w-9 h-9 rounded-xl object-cover" />
                    ) : (
                      <Bot className="h-5 w-5 text-orange-500" />
                    )}
                  </div>
                </Link>
                <div className={`min-w-0 overflow-hidden whitespace-nowrap transition-opacity duration-200 flex-1 ${sidebarCollapsed ? 'opacity-0 w-0' : 'opacity-100'}`}>
                  <div className="flex items-center gap-2">
                    <Link to="/enterprise" className="hover:text-orange-600 transition-colors">
                      <p className="text-sm font-medium text-[#1a1a1a] truncate">{user?.enterpriseName}</p>
                    </Link>
                    {isAdmin && (
                      <Link to="/admin" title={t('管理后台', 'Admin')}
                        className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg bg-orange-500/10 text-orange-500 hover:bg-orange-500/20 transition-colors">
                        <Shield className="h-3.5 w-3.5" />
                        <span className="text-[11px] font-medium">{t('管理', 'Admin')}</span>
                      </Link>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400">{appName}</p>
                </div>
              </div>
            </div>
          )}

          <div className={`${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
            <button
              onClick={handleNewChat}
              title={t('新建对话', 'New Chat')}
              className={`w-full h-9 rounded-xl bg-[#1a1a1a] text-white hover:bg-[#333] flex items-center justify-center transition-all ${sidebarCollapsed ? '' : 'gap-2'}`}>
              <Plus className="h-4 w-4 shrink-0" />
              <span className={`whitespace-nowrap transition-opacity duration-200 ${sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 text-sm font-medium'}`}>
                {t('新建对话', 'New Chat')}
              </span>
            </button>
          </div>

          {/* 对话列表 */}
          <div className={`flex-1 overflow-y-auto px-2 pt-2 transition-opacity duration-200 ${sidebarCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
            {conversations.length > 0 ? (
              <div className="space-y-0.5">
                {conversations.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => {
                      setActiveId(conv.id)
                      setMessages([])
                      setStreamingText('')
                    }}
                    className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all whitespace-nowrap ${
                      activeId === conv.id
                        ? 'bg-orange-50 text-[#1a1a1a] font-medium'
                        : 'text-gray-500 hover:bg-black/[0.03] hover:text-[#1a1a1a]'
                    }`}
                  >
                    <MessageSquare className="h-3.5 w-3.5 inline mr-2 opacity-40" />
                    {conv.titleName}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-300 text-center mt-8">{t('暂无对话', 'No conversations')}</p>
            )}
          </div>

          {/* 升级套餐悬浮入口 */}
          <div className={`${sidebarCollapsed ? 'px-1.5' : 'px-3'} pb-1`}>
            <Link to="/pricing"
              className="group flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-orange-200/40 hover:border-orange-300 hover:shadow-[0_2px_12px_rgba(249,115,22,0.12)] transition-all">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-400 to-amber-400 flex items-center justify-center shrink-0 shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all">
                <Crown className="h-3.5 w-3.5 text-white" />
              </div>
              <span className={`whitespace-nowrap transition-opacity duration-200 text-[13px] font-medium text-orange-600 ${sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                {t('升级套餐', 'Upgrade')}
              </span>
            </Link>
          </div>

          {/* 底部导航抽屉 - 从下往上弹出 */}
          <div className="mt-auto">
            {/* 抽屉内容 - 从底部往上滑出 */}
            <div className={`overflow-hidden transition-[max-height] duration-300 ease-in-out border-t border-black/[0.04] ${navCollapsed ? 'max-h-0 border-t-0' : 'max-h-[500px]'}`}>
              <div className={`${sidebarCollapsed ? 'p-1.5' : 'p-3'} space-y-0.5 bg-gradient-to-b from-black/[0.01] to-transparent`}>
                <Link to="/" title={t('主页', 'Home')}
                  className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 text-sm text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.03] rounded-xl transition-all`}>
                  <Home className="h-5 w-5 shrink-0" />
                  <span className={`whitespace-nowrap transition-opacity duration-200 ${sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>{t('主页', 'Home')}</span>
                </Link>
                <Link to="/orders" title={t('订单记录', 'My Orders')}
                  className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 text-sm text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.03] rounded-xl transition-all`}>
                  <Receipt className="h-5 w-5 shrink-0" />
                  <span className={`whitespace-nowrap transition-opacity duration-200 ${sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>{t('订单记录', 'Orders')}</span>
                </Link>
                <Link to="/generate" title={t('一键生成官网', 'Generate Website')}
                  className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 text-sm text-gray-400 hover:text-orange-500 hover:bg-orange-50/50 rounded-xl transition-all`}>
                  <Sparkles className="h-5 w-5 shrink-0" />
                  <span className={`whitespace-nowrap transition-opacity duration-200 ${sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>{t('一键生成官网', 'Generate Website')}</span>
                </Link>
                <Link to="/my-website" title={t('我的官网', 'My Website')}
                  className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 text-sm text-gray-400 hover:text-orange-500 hover:bg-orange-50/50 rounded-xl transition-all`}>
                  <Globe className="h-5 w-5 shrink-0" />
                  <span className={`whitespace-nowrap transition-opacity duration-200 ${sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>{t('我的官网', 'My Website')}</span>
                </Link>
                <Link to="/knowledge" title={t('知识库管理', 'Knowledge Base')}
                  className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 text-sm text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.03] rounded-xl transition-all`}>
                  <Database className="h-5 w-5 shrink-0" />
                  <span className={`whitespace-nowrap transition-opacity duration-200 ${sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>{t('知识库管理', 'Knowledge Base')}</span>
                </Link>
              </div>
            </div>

            {/* 抽屉触发按钮 - 固定在最底部 */}
            <button onClick={() => setNavCollapsed(!navCollapsed)}
              className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-2'} px-3 py-2.5 text-sm text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.02] transition-all`}>
              <svg className={`h-4 w-4 transition-transform duration-300 ${navCollapsed ? '' : 'rotate-180'}`} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 10l4-4 4 4" />
              </svg>
              <span className={`whitespace-nowrap transition-opacity duration-200 ${sidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>{navCollapsed ? t('展开菜单', 'More') : t('收起菜单', 'Less')}</span>
            </button>
          </div>
        </aside>

        {/* 主聊天区 */}
        <main className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto">
            {messages.length === 0 && !isStreaming ? (
              /* 空状态 */
              <div className="h-full flex flex-col items-center justify-center px-6 max-w-2xl mx-auto">
                <div className="w-12 h-12 rounded-2xl bg-orange-500/10 flex items-center justify-center mb-4">
                  <Bot className="h-6 w-6 text-orange-500" />
                </div>
                <h2 className="text-xl font-semibold text-[#1a1a1a] mb-1">
                  {t(`你好，我是${appName}`, `Hi, I'm ${appName}`)}
                </h2>
                <p className="text-sm text-gray-400 mb-8 text-center">
                  {user?.enterpriseName
                    ? t(`基于${user.enterpriseName}知识库，为你精准解答业务问题`, `Powered by ${user.enterpriseName}'s knowledge base`)
                    : t('基于企业知识库，为你精准解答业务问题', 'Powered by your knowledge base')
                  }
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg">
                  {quickQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleSend(q.label)}
                      className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-black/[0.04] hover:border-orange-200 hover:shadow-sm text-left transition-all group"
                    >
                      <div className={`w-8 h-8 rounded-lg ${q.color} flex items-center justify-center shrink-0`}>
                        <q.icon className="h-4 w-4" />
                      </div>
                      <span className="text-sm text-gray-600 group-hover:text-[#1a1a1a] line-clamp-2">
                        {q.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* 消息列表 */
              <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-lg bg-orange-500/10 flex items-center justify-center mr-2 mt-1 shrink-0">
                        <Bot className="h-4 w-4 text-orange-500" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        msg.role === 'user'
                          ? 'bg-orange-500 text-white rounded-br-md'
                          : 'bg-white border border-black/[0.04] text-[#1a1a1a] rounded-bl-md shadow-sm'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}

                {/* 流式输出中的消息 */}
                {isStreaming && streamingText && (
                  <div className="flex justify-start">
                    <div className="w-7 h-7 rounded-lg bg-orange-500/10 flex items-center justify-center mr-2 mt-1 shrink-0">
                      <Bot className="h-4 w-4 text-orange-500" />
                    </div>
                    <div className="max-w-[80%] px-4 py-3 rounded-2xl rounded-bl-md bg-white border border-black/[0.04] text-[#1a1a1a] shadow-sm text-sm leading-relaxed whitespace-pre-wrap">
                      {streamingText}
                      <span className="inline-block w-1.5 h-4 bg-orange-500 animate-pulse ml-0.5 align-middle" />
                    </div>
                  </div>
                )}

                {/* 等待响应 */}
                {isStreaming && !streamingText && (
                  <div className="flex justify-start">
                    <div className="w-7 h-7 rounded-lg bg-orange-500/10 flex items-center justify-center mr-2 mt-1 shrink-0">
                      <Bot className="h-4 w-4 text-orange-500" />
                    </div>
                    <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-white border border-black/[0.04] shadow-sm">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* 输入栏 - 悬浮效果 */}
          <div className="px-4 pb-4 pt-2">
            <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.03)] focus-within:shadow-[0_4px_24px_rgba(249,115,22,0.12),0_0_0_1px_rgba(249,115,22,0.2)] transition-shadow duration-300">
              {/* 功能开关行 */}
              <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-0.5">
                <button
                  onClick={() => setDeepThink(!deepThink)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                    deepThink
                      ? 'bg-violet-50 text-violet-600 border border-violet-200/60'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50 border border-transparent'
                  }`}>
                  <Zap className="h-3.5 w-3.5" />
                  {t('深度思考', 'Deep Think')}
                </button>
                <div className="relative">
                  <button
                    onClick={() => {
                      if (useKnowledge) {
                        setShowKbConfirm(true)
                      } else {
                        setUseKnowledge(true)
                      }
                    }}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                      useKnowledge
                        ? 'bg-orange-50 text-orange-600 border border-orange-200/60'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50 border border-transparent'
                    }`}>
                    <BookOpen className="h-3.5 w-3.5" />
                    {t('知识库', 'KB')}
                  </button>
                  {showKbConfirm && (
                    <div className="absolute bottom-full left-0 mb-2 w-52 bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.12)] border border-black/[0.06] p-3 z-30">
                      <p className="text-xs text-[#1a1a1a] mb-2 leading-relaxed">
                        {t('关闭知识库后将无法检索企业专属内容，确认关闭？', 'Without KB, enterprise-specific search will be disabled. Confirm?')}
                      </p>
                      <div className="flex gap-2">
                        <button onClick={() => setShowKbConfirm(false)}
                          className="flex-1 px-2.5 py-1.5 text-xs rounded-lg text-gray-500 hover:bg-gray-50 transition-all">
                          {t('取消', 'Cancel')}
                        </button>
                        <button onClick={() => { setUseKnowledge(false); setShowKbConfirm(false) }}
                          className="flex-1 px-2.5 py-1.5 text-xs rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-all">
                          {t('确认关闭', 'Confirm')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setWebSearch(!webSearch)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                    webSearch
                      ? 'bg-blue-50 text-blue-600 border border-blue-200/60'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50 border border-transparent'
                  }`}>
                  <Wifi className="h-3.5 w-3.5" />
                  {t('联网搜索', 'Search')}
                </button>
                <div className="flex-1" />
              </div>

              {/* 输入行 */}
              <div className="flex gap-2 items-end p-2">
                <button
                  className="shrink-0 p-2.5 rounded-xl text-gray-300 hover:text-gray-500 hover:bg-black/[0.03] transition-all"
                  title={t('上传文件', 'Upload file')}
                >
                  <Paperclip className="h-5 w-5" />
                </button>
                <div className="flex-1">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t('输入你的问题...', 'Type your question...')}
                    disabled={isStreaming}
                    className="w-full h-10 px-3 rounded-xl bg-transparent text-[#1a1a1a] placeholder:text-gray-300 focus:outline-none text-sm"
                  />
                </div>
                <Button
                  onClick={() => handleSend()}
                  disabled={!input.trim() || isStreaming}
                  className="shrink-0 h-10 w-10 p-0 bg-orange-500 hover:bg-orange-600 rounded-xl shadow-sm"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
